// Reports (counts and revenue) and Excel export.
import { useCallback, useEffect, useState } from 'react';
import type { Report } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { message } from '../lib/errors.ts';
import { downloadXlsx } from '../lib/excel.ts';
import { shiftDate, todayLocal } from '../lib/time.ts';
import { Banner, Button, Field, Loading, Page, useToast } from '../ui/index.tsx';

function monthStart(date: string) {
  return `${date.slice(0, 8)}01`;
}

function RangePicker({ from, to, onChange }: { from: string; to: string; onChange: (from: string, to: string) => void }) {
  const today = todayLocal();
  return (
    <>
      <div className="filter-chips">
        <button type="button" onClick={() => onChange(today, today)}>วันนี้</button>
        <button type="button" onClick={() => onChange(shiftDate(today, -6), today)}>7 วัน</button>
        <button type="button" onClick={() => onChange(monthStart(today), today)}>เดือนนี้</button>
        <button type="button" onClick={() => { const end = shiftDate(monthStart(today), -1); onChange(monthStart(end), end); }}>เดือนที่แล้ว</button>
      </div>
      <div className="row">
        <Field label="ตั้งแต่"><input type="date" value={from} onChange={(e) => onChange(e.target.value, to)} /></Field>
        <Field label="ถึง"><input type="date" value={to} onChange={(e) => onChange(from, e.target.value)} /></Field>
      </div>
    </>
  );
}

export function Reports() {
  const { api } = useShop();
  const { go } = useShopPaths();
  const today = todayLocal();
  const [range, setRange] = useState({ from: monthStart(today), to: today });
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setReport(null);
    try { setReport(await api.staff.report(range.from, range.to)); } catch (e) { setError(message(e)); }
  }, [api, range]);
  useEffect(() => { void load(); }, [load]);

  const cancelled = (c: Report['total']['counts']) => c.cancelled_customer + c.cancelled_shop;
  return (
    <Page title="รายงาน" back={() => go('/staff/manage')}>
      <RangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
      {error && <Banner kind="error">{error}</Banner>}
      {!report && !error ? <Loading /> : report && (
        <>
          <div className="stat-grid">
            <div className="stat"><span className="muted small">รายได้ (เสร็จแล้ว)</span><b>{report.total.revenue.toLocaleString('th-TH')} ฿</b></div>
            <div className="stat"><span className="muted small">เสร็จแล้ว</span><b>{report.total.counts.completed}</b></div>
            <div className="stat"><span className="muted small">ไม่มา</span><b>{report.total.counts.no_show}</b></div>
            <div className="stat"><span className="muted small">ยกเลิก</span><b>{cancelled(report.total.counts)}</b></div>
          </div>
          <p className="section-title">แยกตามช่าง</p>
          <table className="simple">
            <thead><tr><th>ช่าง</th><th className="num">เสร็จ</th><th className="num">ไม่มา</th><th className="num">รายได้</th></tr></thead>
            <tbody>
              {report.byBarber.map((b) => (
                <tr key={b.barberId}><td>{b.barberName}</td><td className="num">{b.counts.completed}</td><td className="num">{b.counts.no_show}</td><td className="num">{b.revenue.toLocaleString('th-TH')}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Page>
  );
}

export function Export() {
  const { api, shop, liff } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const today = todayLocal();
  const [range, setRange] = useState({ from: monthStart(today), to: today });
  const [busy, setBusy] = useState<string | null>(null);
  const inLine = liff.isInClient();

  async function run(kind: 'bookings' | 'customers') {
    setBusy(kind);
    try {
      if (kind === 'bookings') {
        await downloadXlsx(await api.staff.exportBookings(range.from, range.to), `bookings-${shop.slug}-${range.from}_${range.to}.xlsx`);
      } else {
        await downloadXlsx(await api.staff.exportCustomers(), `customers-${shop.slug}-${today}.xlsx`);
      }
      toast('ดาวน์โหลดแล้ว', 'success');
    } catch (e) {
      toast(message(e), 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Page title="ส่งออก Excel" back={() => go('/staff/manage')}>
      {inLine && (
        <Banner kind="info" action={{ label: 'เปิดในเบราว์เซอร์', onClick: () => liff.openWindow({ url: window.location.href, external: true }) }}>
          แอป LINE อาจดาวน์โหลดไฟล์ไม่ได้ เปิดในเบราว์เซอร์ของมือถือแทน
        </Banner>
      )}
      <RangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
      <div className="stack">
        <Button block loading={busy === 'bookings'} disabled={busy !== null} onClick={() => void run('bookings')}>ส่งออกการจอง (.xlsx)</Button>
        <Button block variant="secondary" loading={busy === 'customers'} disabled={busy !== null} onClick={() => void run('customers')}>ส่งออกรายชื่อลูกค้า (.xlsx)</Button>
      </div>
    </Page>
  );
}
